import { ChangeEvent, forwardRef, useState } from 'react';

import { Box, CloseButton, Textarea, TextareaProps } from '@mantine/core';

import LabelText, { LabelTextProps } from '../LabelText';

import styles from './TextareaWrapper.module.scss';

export interface TextareaWrapperProps extends TextareaProps {
  showLabel?: boolean;
  label?: string;
  labelProps?: Partial<LabelTextProps>;
  className?: string;
  showClear?: boolean;
  onClear?: () => void;
}

const TextareaWrapper = forwardRef<HTMLTextAreaElement, TextareaWrapperProps>(
  (
    { showLabel = true, label, labelProps, className, showClear = false, onClear, ...rest },
    ref
  ) => {
    const [localValue, setLocalValue] = useState<string>(String(rest.value || ''));

    const currentValue = rest.value !== undefined ? rest.value : localValue;

    const textValue = String(currentValue || '');

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      rest.onChange?.(event);

      if (rest.value === undefined) {
        setLocalValue(event.currentTarget.value);
      }
    };

    const handleClear = () => {
      if (onClear) {
        onClear();
      } else {
        if (rest.onChange) {
          const syntheticEvent = {
            target: { value: '' },
            currentTarget: { value: '' }
          } as ChangeEvent<HTMLTextAreaElement>;
          rest.onChange(syntheticEvent);
        }
        setLocalValue('');
      }
    };

    return (
      <Box>
        {showLabel && label && <LabelText label={label} variant="regular" {...labelProps} />}

        <div className={styles.textareaContainer}>
          <Textarea
            ref={ref}
            {...rest}
            value={currentValue}
            onChange={handleChange}
            classNames={{
              input: `${styles.textarea} ${className || ''}`
            }}
            onKeyDown={rest.onKeyDown}
          />

          {showClear && textValue && (
            <CloseButton
              aria-label="Clear textarea"
              onClick={handleClear}
              className={styles.clearButton}
            />
          )}

          {rest.maxLength && (
            <div className={styles.charCount}>
              {textValue.length} / {rest.maxLength}
            </div>
          )}
        </div>
      </Box>
    );
  }
);

TextareaWrapper.displayName = 'TextareaWrapper';

export default TextareaWrapper;
